import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import './App.css';
import './StatsView.css';

// Stats interfaces based on modules_heart.py
interface InferenceStats {
    extracted_count: number;
    not_extracted_count: number;
    extraction_failed_count: number;
    embedded_count: number;
    not_embedded_count: number;
    embedding_failed_count: number;
    meta_data_count: number;
    not_meta_data_count: number;
    meta_data_failed_count: number;
}

interface SearchResultStats {
    search_results_count: number;
    urls_count: number;
    processed_results_count: number;
    prepared_results_count: number;
    pdf_urls_does_not_exist_count: number;
    no_pdf_urls_count: number;
    none_pdf_urls_count: number;
    checked_on_pdf_count: number;
    failed_check_on_pdf_count: number;
    empty_pdf_urls_array_count: number;
    not_checked_on_pdf_count: number;
}

interface SearchTargetStats {
    search_target_count: number;
    not_searched_count: number;
    searched_count: number;
    no_source_count: number;
    no_type_count: number;
}

interface DownloadStats {
    report_source_count: number;
    is_pdf_true_count: number;
    is_pdf_false_count: number;
    download_count: number;
    not_downloaded_count: number;
    download_failed_count: number;
    not_found_count: number;
    filename_none_count: number;
    filename_count: number;
    discrepancy_in_number_of_documents: number;
}

interface TitleValidationStats {
    validated_count: number;
    not_validated_count: number;
    validation_failed_count: number;
    validation_passed_count: number;
    title_found_count: number;
    title_not_found_count: number;
    title_with_confidence_count: number;
    average_validation_score: number;
    average_title_confidence: number;
    high_confidence_title_count: number;
    medium_confidence_title_count: number;
    low_confidence_title_count: number;
}

interface StatsData {
    inference_stats: InferenceStats;
    search_result_stats: SearchResultStats;
    search_target_stats: SearchTargetStats;
    download_stats: DownloadStats;
    title_validation_stats: TitleValidationStats;
    last_updated: string;
}

const StatsView: React.FC = () => {
    const { token, isAuthenticated, authLoaded } = useAuth();
    const { t } = useTranslation();
    const [statsData, setStatsData] = useState<StatsData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const apiUrl = env.apiUrl;

    const fetchStats = async () => {
        if (!token) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.get(`${apiUrl}/admin/stats`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            setStatsData(response.data);
        } catch (err: any) {
            console.error('Error fetching stats:', err);
            setError(err.response?.data?.error || 'Failed to fetch statistics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authLoaded && isAuthenticated && token) {
            fetchStats();
        }
    }, [authLoaded, isAuthenticated, token]);

    const StatCard: React.FC<{ title: string; children: React.ReactNode; icon: string; color: string }> = ({ 
        title, children, icon, color 
    }) => (
        <div className="stats-card">
            <div className="stats-card-header" style={{ borderLeftColor: color }}>
                <i className={`fas ${icon}`} style={{ color }}></i>
                <h3>{title}</h3>
            </div>
            <div className="stats-card-content">
                {children}
            </div>
        </div>
    );

    const StatItem: React.FC<{ label: string; value: number | string; type?: 'number' | 'percentage' }> = ({ 
        label, value, type = 'number' 
    }) => (
        <div className="stat-item">
            <span className="stat-label">{label}</span>
            <span className="stat-value">
                {type === 'percentage' ? `${Number(value).toFixed(2)}%` : Number(value).toLocaleString()}
            </span>
        </div>
    );

    const ProgressBar: React.FC<{ label: string; value: number; total: number; color?: string }> = ({ 
        label, value, total, color = '#2196F3' 
    }) => {
        const percentage = total > 0 ? (value / total) * 100 : 0;
        return (
            <div className="progress-item">
                <div className="progress-header">
                    <span className="progress-label">{label}</span>
                    <span className="progress-value">{value.toLocaleString()} / {total.toLocaleString()}</span>
                </div>
                <div className="progress-bar-container">
                    <div 
                        className="progress-bar-fill" 
                        style={{ width: `${percentage}%`, backgroundColor: color }}
                    ></div>
                </div>
                <div className="progress-percentage">{percentage.toFixed(1)}%</div>
            </div>
        );
    };

    if (!authLoaded || !isAuthenticated) {
        return (
            <div className="container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>{t('stats.loadingAuth')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container stats-dashboard">
            <div className="page-header">
                <h1>
                    <i className="fas fa-chart-bar"></i>
                    {t('stats.title')}
                </h1>
                <p className="page-subtitle">
                    {t('stats.subtitle')}
                </p>
                {statsData?.last_updated && (
                    <div className="last-updated">
                        <i className="fas fa-clock"></i>
                        {t('stats.lastUpdated')}: {new Date(statsData.last_updated).toLocaleString()}
                    </div>
                )}
                <button 
                    onClick={fetchStats} 
                    disabled={loading}
                    className="refresh-button"
                >
                    <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                    {t('stats.refreshData')}
                </button>
            </div>

            {error && (
                <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {error}
                </div>
            )}

            {loading && (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>{t('stats.loadingStats')}</p>
                </div>
            )}

            {!loading && !error && statsData && (
                <div className="stats-grid">
                    {/* Inference Statistics */}
                    <StatCard title={t('stats.documentProcessing')} icon="fa-cogs" color="#4CAF50">
                        <div className="stats-section">
                            <h4>Extraction</h4>
                            <ProgressBar 
                                label="Extracted" 
                                value={statsData.inference_stats.extracted_count} 
                                total={statsData.inference_stats.extracted_count + statsData.inference_stats.not_extracted_count}
                                color="#4CAF50"
                            />
                            <StatItem label="Failed Extractions" value={statsData.inference_stats.extraction_failed_count} />
                        </div>
                        
                        <div className="stats-section">
                            <h4>Embedding</h4>
                            <ProgressBar 
                                label="Embedded" 
                                value={statsData.inference_stats.embedded_count} 
                                total={statsData.inference_stats.embedded_count + statsData.inference_stats.not_embedded_count}
                                color="#2196F3"
                            />
                            <StatItem label="Failed Embeddings" value={statsData.inference_stats.embedding_failed_count} />
                        </div>

                        <div className="stats-section">
                            <h4>Metadata</h4>
                            <ProgressBar 
                                label="Processed" 
                                value={statsData.inference_stats.meta_data_count} 
                                total={statsData.inference_stats.meta_data_count + statsData.inference_stats.not_meta_data_count}
                                color="#FF9800"
                            />
                            <StatItem label="Failed Metadata" value={statsData.inference_stats.meta_data_failed_count} />
                        </div>
                    </StatCard>

                    {/* Download Statistics */}
                    <StatCard title={t('stats.documentDownloads')} icon="fa-download" color="#2196F3">
                        <StatItem label="Total Report Sources" value={statsData.download_stats.report_source_count} />
                        
                        <div className="stats-section">
                            <h4>Document Types</h4>
                            <ProgressBar 
                                label="PDF Documents" 
                                value={statsData.download_stats.is_pdf_true_count} 
                                total={statsData.download_stats.is_pdf_true_count + statsData.download_stats.is_pdf_false_count}
                                color="#f44336"
                            />
                        </div>

                        <div className="stats-section">
                            <h4>Download Status</h4>
                            <ProgressBar 
                                label="Downloaded" 
                                value={statsData.download_stats.download_count} 
                                total={statsData.download_stats.download_count + statsData.download_stats.not_downloaded_count}
                                color="#4CAF50"
                            />
                            <StatItem label="Download Failures" value={statsData.download_stats.download_failed_count} />
                            <StatItem label="Not Found" value={statsData.download_stats.not_found_count} />
                        </div>

                        <div className="stats-section">
                            <h4>Filenames</h4>
                            <ProgressBar 
                                label="With Filename" 
                                value={statsData.download_stats.filename_count} 
                                total={statsData.download_stats.filename_count + statsData.download_stats.filename_none_count}
                                color="#9C27B0"
                            />
                        </div>
                    </StatCard>

                    {/* Search Results Statistics */}
                    <StatCard title={t('stats.searchResults')} icon="fa-search" color="#FF9800">
                        <StatItem label="Total Search Results" value={statsData.search_result_stats.search_results_count} />
                        <StatItem label="URLs in Results" value={statsData.search_result_stats.urls_count} />
                        
                        <div className="stats-section">
                            <h4>Processing Status</h4>
                            <ProgressBar 
                                label="Processed" 
                                value={statsData.search_result_stats.processed_results_count} 
                                total={statsData.search_result_stats.search_results_count}
                                color="#4CAF50"
                            />
                            <StatItem label="Prepared Results" value={statsData.search_result_stats.prepared_results_count} />
                        </div>

                        <div className="stats-section">
                            <h4>PDF Status</h4>
                            <StatItem label="No PDF URLs" value={statsData.search_result_stats.no_pdf_urls_count} />
                            <StatItem label="Empty PDF Arrays" value={statsData.search_result_stats.empty_pdf_urls_array_count} />
                            <ProgressBar 
                                label="PDF Checked" 
                                value={statsData.search_result_stats.checked_on_pdf_count} 
                                total={statsData.search_result_stats.checked_on_pdf_count + statsData.search_result_stats.not_checked_on_pdf_count}
                                color="#2196F3"
                            />
                            <StatItem label="PDF Check Failures" value={statsData.search_result_stats.failed_check_on_pdf_count} />
                        </div>
                    </StatCard>

                    {/* Search Targets Statistics */}
                    <StatCard title={t('stats.searchTargets')} icon="fa-bullseye" color="#9C27B0">
                        <StatItem label="Total Search Targets" value={statsData.search_target_stats.search_target_count} />
                        
                        <div className="stats-section">
                            <h4>Search Status</h4>
                            <ProgressBar 
                                label="Searched" 
                                value={statsData.search_target_stats.searched_count} 
                                total={statsData.search_target_stats.search_target_count}
                                color="#4CAF50"
                            />
                            <StatItem label="Not Searched" value={statsData.search_target_stats.not_searched_count} />
                        </div>

                        <div className="stats-section">
                            <h4>Data Quality</h4>
                            <StatItem label="Missing Source" value={statsData.search_target_stats.no_source_count} />
                            <StatItem label="Missing Type" value={statsData.search_target_stats.no_type_count} />
                        </div>
                    </StatCard>

                    {/* Title Validation Statistics */}
                    <StatCard title={t('stats.titleValidation')} icon="fa-check-circle" color="#f44336">
                        <div className="stats-section">
                            <h4>Validation Status</h4>
                            <ProgressBar 
                                label="Validated" 
                                value={statsData.title_validation_stats.validated_count} 
                                total={statsData.title_validation_stats.validated_count + statsData.title_validation_stats.not_validated_count}
                                color="#4CAF50"
                            />
                            <ProgressBar 
                                label="Passed" 
                                value={statsData.title_validation_stats.validation_passed_count} 
                                total={statsData.title_validation_stats.validation_passed_count + statsData.title_validation_stats.validation_failed_count}
                                color="#2196F3"
                            />
                        </div>

                        <div className="stats-section">
                            <h4>Title Detection</h4>
                            <ProgressBar 
                                label="Titles Found" 
                                value={statsData.title_validation_stats.title_found_count} 
                                total={statsData.title_validation_stats.title_found_count + statsData.title_validation_stats.title_not_found_count}
                                color="#FF9800"
                            />
                            <StatItem label="With Confidence Scores" value={statsData.title_validation_stats.title_with_confidence_count} />
                        </div>

                        <div className="stats-section">
                            <h4>Quality Metrics</h4>
                            <StatItem label="Avg Validation Score" value={statsData.title_validation_stats.average_validation_score} type="percentage" />
                            <StatItem label="Avg Title Confidence" value={statsData.title_validation_stats.average_title_confidence} type="percentage" />
                        </div>

                        <div className="stats-section">
                            <h4>Confidence Distribution</h4>
                            <StatItem label="High Confidence (≥80%)" value={statsData.title_validation_stats.high_confidence_title_count} />
                            <StatItem label="Medium Confidence (50-80%)" value={statsData.title_validation_stats.medium_confidence_title_count} />
                            <StatItem label="Low Confidence (<50%)" value={statsData.title_validation_stats.low_confidence_title_count} />
                        </div>
                    </StatCard>
                </div>
            )}
        </div>
    );
};

export default StatsView; 